/* The feature registry — designs/feature-surface.md FS1/FS5. Features are
   born "dev" and promoted to "release" by a one-line surface flip in the PR
   that finishes them — code, not config, so "what does prod show?" is a
   git-log question and every entry sits under the coverage gate. */

export type Surface = "release" | "dev";

/* One entry per hideable feature. `routePrefixes` names the API path
   prefixes the server route gate will own for the feature (FS3 — PR 2);
   client-side, drawer sections, area tabs, card rows, and menu ops carry a
   `featureId` and render through `enabled()`. */
export interface Feature {
  id: FeatureId;
  surface: Surface;
  routePrefixes?: readonly string[];
}

/* BULK PROMOTION 2026-09-03 (Lewis, with Trevor + the managers): the dev
   surface was demoed and blessed as the starting baseline, so every entry
   below that was born "dev" is PROMOTED to "release" in one pass — prod now
   shows the full surface. From here the model reverts to normal: each NEW
   feature is born "dev", built and tested on dev in its own PR, then flipped
   to "release" in that PR (no more letting a huge change pile up). The
   per-entry comments below describe each feature's ORIGINAL birth/scope; the
   surface value is the current truth. */

/* HISTORY — superseded by the 2026-09-03 bulk promotion above (every entry is
   now "release"). Before that, features reached release incrementally: my-work
   (designs/my-work.md MA10), roles-catalog (canonical-roles.md CR10), and the
   project Charter + Roster (charter-and-roster.md RC2/RC31) were each promoted
   by the PR that built them; the portfolio sections + their routes, Mappings,
   intake, and the stub area tabs were still dev at that point. Kept for the
   trail — not a description of the current surface values below. */
const REGISTRY = [
  /* Products / Platforms: drawer sections, directory trees, New-draft ops,
     and the /api/portfolio routes — one feature, promoted together (the
     sections are one section-navigator family over one API). Prototype is
     retired from the vocabulary (portfolio-mapping.md P11) — no drawer
     section of its own, but existing prototype-kind rows and mappings still
     ride this same feature id and API. */
  { id: "portfolio", surface: "release", routePrefixes: ["/api/portfolio"] },
  /* project Mappings: the area tab, the Maps-to card row, the Edit-mappings
     button, and the /api/mappings routes */
  { id: "mappings", surface: "release", routePrefixes: ["/api/mappings"] },
  /* the Charter area — PROMOTED (designs/charter-and-roster.md RC2). The
     project's start and end month are editable on it and nowhere else, and
     they were editable on the release surface (the Edit form) before this:
     gating the only remaining editor would make a released, recorded fact
     uneditable in production. FS6's "hide tabs with nothing behind them"
     stops applying the moment the tab has something behind it. */
  { id: "project-charter", surface: "release" },
  /* the Roster area — PROMOTED (designs/charter-and-roster.md RC31), and
     listed here EXPLICITLY rather than shipping by absence. It already
     reached production either way: `enabledIn` shows an unregistered id on
     every surface, and the Roster is a RE-HOMING of "who is on this project"
     from Resourcing, content that ships on release today. But absence
     recorded nothing — the registry could not answer "is the Roster in
     release?" except by failing to mention it. Trevor, Aug 28 2026: "yes
     explicit addition". NO routePrefixes, for the roles-catalog reason: the
     Roster adds no server routes at all — this whole PR makes no server,
     store, schema or API change — so a prefix would gate paths that do not
     exist. */
  { id: "project-roster", surface: "release" },
  /* stub area tabs — project record (FS6: the honest "planned" notes stay,
     in dev; each tab flips to release in the PR that builds its area) */
  { id: "project-roles-raci", surface: "release" },
  { id: "project-deliverables", surface: "release" },
  { id: "project-process", surface: "release" },
  { id: "project-change-request", surface: "release" },
  /* stub area tabs — team record */
  { id: "team-org-chart", surface: "release" },
  { id: "team-charge-codes", surface: "release" },
  /* the FTE journey (FS10, designs/my-work.md MA10) — born dev as
     my-allocations with its route family reserved (/api/my, never
     /api/me/…), renamed and promoted in the PR that built the page. This
     flip is also what moved the landing (MA2's derivation reads it). */
  { id: "my-work", surface: "release", routePrefixes: ["/api/my"] },
  /* the canonical role catalog (designs/canonical-roles.md CR9/CR10): the
     drawer section only — NO routePrefixes, because release-1's project and
     allocation forms are built from GET /api/roles and gating it would 404 a
     route they depend on. The write is protected by authority (CR8).
     PROMOTED Aug 28 2026 — Trevor's scope call, the promotion CR10 deferred
     to an owner: the catalog is built, so the section it gates now stands on
     the release surface. The absent routePrefixes are unchanged by that — CR9
     was never a surface question. */
  { id: "roles-catalog", surface: "release" },
  /* the canonical team list API (canonical-teams.md CT6) — the first intake
     fold-in surface, born dev (intake-foldin.md IF3); release 404s the
     whole /api/intake/teams family */
  { id: "intake-teams", surface: "release", routePrefixes: ["/api/intake/teams"] },
  /* the intake module itself (intake-foldin.md IF3/IF7) — the WS3 React
     surface: domain tabs, capture form, groom board. Registered at birth so
     it can NEVER reach production by omission (an unregistered id shows on
     every surface, FS7). Its route family grows with the WS2 PRs — the
     capability routes (+ their satellite subroutes) ride this prefix. */
  { id: "intake", surface: "release", routePrefixes: ["/api/intake/capabilities"] },
  /* the "Out of the Menus" navigation redesign (designs/navigation-redesign.md
     NR1): the record heading's stepper + primary-action row replacing File ▾,
     and Part 2's persistent filterable portfolio replacing the modal picker.
     Born dev; the release surface keeps the current File-menu/picker chrome
     untouched until the promote PR that carries Trevor's sign-off. NO
     routePrefixes: pure client — the data model and the state-transition
     endpoints do not change. */
  { id: "nav-redesign", surface: "release" },
  /* the help layer (designs/help-layer.md HL4): the About Hermes section,
     the per-section About panels, and the concept/hover layer — born dev,
     promoted with Trevor's guard-1 sign-off. NO routePrefixes: pure client. */
  { id: "about-hermes", surface: "release" },
  /* disposition on a groomed intake ask (designs/intake-project-mapping.md
     IPM7): the drawer's chooser. (IPM8's project-record "Intake asks" row
     was cut before merge — this gates only the chooser, which is the only
     new client control that actually shipped.) Born `release` directly —
     complete and tested in this one PR (the Nuance-2 born-release
     criterion). NO routePrefixes: every route it touches
     (/api/intake/capabilities/*, /api/projects) already belongs to the
     released intake/core-project routes — a prefix here would gate paths
     this feature doesn't own. Gates the chooser as a kill switch if it
     needs pulling without a revert. */
  { id: "intake-project-mapping", surface: "release" },
  /* the PDP template catalog (designs/pdp-catalog.md PC1): the PDP drawer
     section and the /api/pdp route family. Born dev and PROMOTED in the same
     PR — the one that finished use case 1 (pdp-catalog.md is PR 1 of the PDP
     ladder), so the catalog stands on release while the Products PRs build
     against it on dev. The prefix stays so the family can be pulled as one
     kill switch. */
  { id: "pdp-catalog", surface: "release", routePrefixes: ["/api/pdp"] },
  /* the KPI feed seam (designs/kpi-feed-seam.md KF11): the /api/feeds
     routes — sources, the free logged reading, the one network read. Born
     dev with the server module (UC 7 back half, Sep 23 2026); PR 5 of the
     PDP ladder (the feed rows in the UI, Sep 25 2026) finished use case 7
     and PROMOTED it. The prefix stays so the family can be pulled as one
     kill switch. */
  { id: "pdp-feeds", surface: "release", routePrefixes: ["/api/feeds"] },
  /* the Products section over the PDP (designs/products-pdp.md PP14): the
     drawer section between Projects and Teams, its Phase / Deliverables
     areas and the /api/products route family (what hangs UNDER a product;
     the product list itself stays /api/intake/products, untouched). Born dev
     with PR 2 (declaration); PR 3 added evidence; PR 4 (gate reviews and
     certificates, Sep 25 2026) finished use cases 2–6 and PROMOTED it. The
     prefix stays so the family can be pulled as one kill switch. */
  { id: "products", surface: "release", routePrefixes: ["/api/products"] },
  /* the issue tracker (designs/issue-tracker.md IT11): the Report button in
     the header, the Issues section, and the /api/issues routes. Born
     release — complete in one PR, and Hermes is still in its development
     phase (intake-ownership-model.md OM9). The prefix keeps the family one
     kill switch. */
  { id: "issues", surface: "release", routePrefixes: ["/api/issues"] },
  /* product baselines (designs/product-baselines.md BL14/BL20): the Baselines
     section, its templates, and both route families. Born dev — the reach
     stacker team runs its first KPI baseline on the dev site before anyone
     decides who may change a draft (BL17). */
  { id: "baselines", surface: "dev", routePrefixes: ["/api/baselines", "/api/baseline-templates"] },
] as const;

export type FeatureId = (typeof REGISTRY)[number]["id"];

export const FEATURES: readonly Feature[] = REGISTRY;

/* The rule over any registry — the seam that keeps the promoted-feature
   branch testable while release-1 has no promoted entries yet (FS7). An id
   the registry does not list is not hideable: shown on every surface. */
export function enabledIn(features: readonly Feature[], id: string, surface: Surface): boolean {
  const feature = features.find((f) => f.id === id);
  return feature === undefined || feature.surface === "release" || surface === "dev";
}

export function enabled(id: FeatureId, surface: Surface): boolean {
  return enabledIn(FEATURES, id, surface);
}
